import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { Card, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import useAdvanceTable from 'hooks/useAdvanceTable';
import objectivesService from 'services/objectivesService';

// Utilidades de fechas
const getDaysDiffInclusive = (start, end) => {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diff = Math.floor((endDate - startDate) / MS_PER_DAY) + 1;
  return Math.max(0, diff);
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Cálculo de esperado genérico: crece linealmente entre start y end
const calculateExpectedUntilYesterday = (objective) => {
  try {
    const { startDate, endDate, nominalValue, unit } = objective || {};
    const normalizedUnit = normalizeUnit(unit);

    if (COST_PER_UNITS.has(normalizedUnit)) {
      return Number(nominalValue || 0);
    }

    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Hasta AYER
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (yesterday < start) return 0;
    if (yesterday >= end) return Number(nominalValue || 0);

    const totalDays = getDaysDiffInclusive(start, end);
    const elapsedDays = getDaysDiffInclusive(start, yesterday);
    if (totalDays <= 0) return 0;
    return Number(nominalValue || 0) * (elapsedDays / totalDays);
  } catch (_) {
    return 0;
  }
};

const COST_PER_UNITS = new Set(['CPA', 'CPM', 'CPV']);

const normalizeUnit = (unit) => (unit || '').toUpperCase();

const formatNumber = (value, { currency = false, decimals = 2, allowZero = false } = {}) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '—';
  if (numericValue === 0 && !allowZero) return '—';
  const options = {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  };
  if (currency) {
    return `$${numericValue.toLocaleString('en-US', options)}`;
  }
  return numericValue.toLocaleString('en-US', options);
};

const formatUnitValue = (unit, value, { allowZero = false } = {}) => {
  const normalized = normalizeUnit(unit);
  const currencyUnits = COST_PER_UNITS;
  const formatted = formatNumber(value, {
    currency: currencyUnits.has(normalized),
    decimals: currencyUnits.has(normalized) ? 2 : 0,
    allowZero,
  });
  if (formatted === '—') return formatted;
  return `${formatted} ${unit || ''}`.trim();
};

const computeSummaryValue = (unit, summary = {}) => {
  const normalized = normalizeUnit(unit);
  const totals = {
    leads: Number(summary.totalLeadsReal || 0),
    onsiteLeads: Number(summary.totalOnsiteLeadsReal || 0),
    websiteRegistrations: Number(summary.totalWebsiteRegistrationsReal || 0),
    whatsapp: Number(summary.totalWhatsappReal || 0),
    clicks: Number(summary.totalClicksReal || 0),
    linkClicks: Number(summary.totalLinkClicksReal || 0),
    impressions: Number(summary.totalImpressionsReal || 0),
    videoViews: Number(summary.totalVideoViewsReal || 0),
    reach: Number(summary.totalReachReal || 0),
  };
  const totalCost = Number(summary.totalCostReal || 0);

  switch (normalized) {
    case 'CLICKS':
      return totals.clicks;
    case 'LINK CLICKS':
      return totals.linkClicks;
    case 'IMPRESIONES':
      return totals.impressions;
    case 'WHATSAPP':
      return totals.whatsapp;
    case 'REGISTROS WEB':
      return totals.websiteRegistrations;
    case 'VISTAS DE VIDEO':
      return totals.videoViews;
    case 'REACH':
      return totals.reach;
    case 'CPA':
      return totals.leads > 0 ? totalCost / totals.leads : 0;
    case 'CPM':
      return totals.impressions > 0 ? (totalCost / totals.impressions) * 1000 : 0;
    case 'CPV':
      return totals.videoViews > 0 ? totalCost / totals.videoViews : 0;
    default:
      return totals.leads;
  }
};

const calculateProgressPctForUnit = (unit, expected, real) => {
  const normalized = normalizeUnit(unit);
  const expectedValue = Number(expected || 0);
  const realValue = Number(real || 0);
  if (expectedValue <= 0 || realValue <= 0) return 0;
  if (COST_PER_UNITS.has(normalized)) {
    return (expectedValue / realValue) * 100;
  }
  return (realValue / expectedValue) * 100;
};

const getSubObjectiveRealValue = (unit, totals, totalCost) => {
  const normalized = normalizeUnit(unit);
  switch (normalized) {
    case 'CLICKS':
      return totals.clicks;
    case 'LINK CLICKS':
      return totals.linkClicks || 0;
    case 'IMPRESIONES':
      return totals.impressions;
    case 'WHATSAPP':
      return totals.whatsapp || 0;
    case 'REGISTROS WEB':
      return totals.websiteRegistrations || 0;
    case 'LEADS':
      return totals.leads;
    case 'VISTAS DE VIDEO':
      return totals.videoViews;
    case 'REACH':
      return totals.reach || 0;
    case 'CPA':
      return totals.leads > 0 ? totalCost / totals.leads : 0;
    case 'CPM':
      return totals.impressions > 0 ? (totalCost / totals.impressions) * 1000 : 0;
    case 'CPV':
      return totals.videoViews > 0 ? totalCost / totals.videoViews : 0;
    default:
      return totals.leads;
  }
};

const getSupplementalColumnMeta = (unit, totals, totalCost) => {
  const normalized = normalizeUnit(unit);
  switch (normalized) {
    case 'CLICKS':
      return {
        label: 'CPC',
        value: totals.clicks > 0 ? totalCost / totals.clicks : 0,
        currency: true,
        allowZero: false,
      };
    case 'LINK CLICKS':
      return {
        label: 'Link CPC',
        value: (totals.linkClicks || 0) > 0 ? totalCost / totals.linkClicks : 0,
        currency: true,
        allowZero: false,
      };
    case 'WHATSAPP':
      return {
        label: 'CP-Conv',
        value: (totals.whatsapp || 0) > 0 ? totalCost / totals.whatsapp : 0,
        currency: true,
        allowZero: false,
      };
    case 'REGISTROS WEB':
      return {
        label: 'CP-Reg',
        value:
          (totals.websiteRegistrations || 0) > 0
            ? totalCost / totals.websiteRegistrations
            : 0,
        currency: true,
        allowZero: false,
      };
    case 'IMPRESIONES':
      return {
        label: 'CPM',
        value: totals.impressions > 0 ? (totalCost / totals.impressions) * 1000 : 0,
        currency: true,
        allowZero: false,
      };
    case 'CPM':
      return {
        label: 'Impresiones',
        value: totals.impressions,
        currency: false,
        allowZero: true,
        decimals: 0,
      };
    case 'CPV':
      return {
        label: 'Vistas',
        value: totals.videoViews,
        currency: false,
        allowZero: true,
        decimals: 0,
      };
    case 'CPA':
      return {
        label: 'Leads',
        value: totals.leads,
        currency: false,
        allowZero: true,
        decimals: 0,
      };
    default:
      return {
        label: 'CPL',
        value: totals.leads > 0 ? totalCost / totals.leads : 0,
        currency: true,
        allowZero: false,
      };
  }
};

const getCampaignMetricMeta = (unit, campaign) => {
  const normalized = normalizeUnit(unit);
  switch (normalized) {
    case 'CLICKS':
      return { value: campaign.clicks || 0, currency: false, decimals: 0 };
    case 'LINK CLICKS':
      return { value: campaign.linkClicks || 0, currency: false, decimals: 0 };
    case 'IMPRESIONES':
      return { value: campaign.impressions || 0, currency: false, decimals: 0 };
    case 'WHATSAPP':
      return { value: campaign.whatsapp || 0, currency: false, decimals: 0 };
    case 'REGISTROS WEB':
      return {
        value: campaign.websiteRegistrations || 0,
        currency: false,
        decimals: 0,
      };
    case 'LEADS':
      return { value: campaign.leads || 0, currency: false, decimals: 0 };
    case 'VISTAS DE VIDEO':
      return { value: campaign.videoViews || 0, currency: false, decimals: 0 };
    case 'REACH':
      return { value: campaign.reach || 0, currency: false, decimals: 0 };
    case 'CPM': {
      const impressions = campaign.impressions || 0;
      const value = impressions > 0 ? (campaign.cost || 0) / impressions * 1000 : 0;
      return { value, currency: true, decimals: 2 };
    }
    case 'CPV': {
      const videoViews = campaign.videoViews || 0;
      const value =
        campaign.cpv ||
        (videoViews > 0 ? (campaign.cost || 0) / videoViews : 0);
      return { value, currency: true, decimals: 2 };
    }
    case 'CPA': {
      const leads = campaign.leads || 0;
      const value = campaign.cpl || (leads > 0 ? (campaign.cost || 0) / leads : 0);
      return { value, currency: true, decimals: 2 };
    }
    default:
      return { value: campaign.leads || 0, currency: false, decimals: 0 };
  }
};

const columns = [
  { 
    accessorKey: 'expand', 
    header: '', 
    cell: ({ row }) => (
      row.original.__hasSubObjectives ? (
        <button 
          className="btn btn-sm btn-link p-0" 
          onClick={() => row.original.__toggleExpand && row.original.__toggleExpand()}
        >
          <FontAwesomeIcon 
            icon={row.original.__isExpanded ? 'chevron-down' : 'chevron-right'} 
            className="text-primary"
          />
        </button>
      ) : null
    ),
    size: 30
  },
  { accessorKey: 'clientName', header: 'Cliente' },
  { accessorKey: 'accountName', header: 'Cuenta' },
  { accessorKey: 'medium', header: 'Medio', cell: ({ row }) => (
    <Badge bg="light" text="dark">{row.original.medium || 'N/A'}</Badge>
  ) },
  { accessorKey: 'period', header: 'Período', cell: ({ row }) => {
    const s = row.original.startDate; const e = row.original.endDate;
    const fmt = (dateStr) => {
      if (!dateStr) return '';
      try {
        // Si es una fecha ISO string UTC, convertir correctamente a fecha local
        if (dateStr.includes('T')) {
          const date = new Date(dateStr);
          // Usar UTC para evitar problemas de zona horaria
          const year = date.getUTCFullYear();
          const month = String(date.getUTCMonth() + 1).padStart(2, '0');
          const day = String(date.getUTCDate()).padStart(2, '0');
          return `${day}/${month}/${year}`;
        }
        // Si ya está en formato YYYY-MM-DD, convertir también
        if (dateStr.includes('-') && dateStr.length === 10) {
          const [year, month, day] = dateStr.split('-');
          return `${day}/${month}/${year}`;
        }
        return dateStr;
      } catch (e) {
        return dateStr;
      }
    };
    return `${fmt(s)} - ${fmt(e)}`;
  } },
  { accessorKey: 'objective', header: 'Objetivo', cell: ({ row }) => (
    <span className="fw-semibold">{(row.original.nominalValue || 0).toLocaleString()} {row.original.unit || ''}</span>
  ) },
  { accessorKey: 'expected', header: 'Esperado (hasta ayer)', cell: ({ row }) => (
    <span className="text-700">{row.original.__expectedDisplay}</span>
  ) },
  { accessorKey: 'real', header: 'Real', cell: ({ row }) => {
    const value = row.original.__realDisplay;
    return <span className="fw-semibold">{value}</span>;
  } },
  { accessorKey: 'progress', header: 'Progreso', cell: ({ row }) => {
    const pct = clamp(row.original.__progressPct || 0, 0, 999);
    let variant = 'secondary';
    if (pct >= 90 && pct <= 110) variant = 'success';
    else if (pct < 90) variant = 'warning';
    else if (pct > 110) variant = 'danger';
    return (
      <Badge bg={variant} className="fs-11">
        <FontAwesomeIcon icon={pct > 110 ? 'arrow-up' : pct < 90 ? 'arrow-down' : 'check'} className="me-1" />
        {pct.toFixed(1)}%
      </Badge>
    );
  } },
];

const ObjectivesTrackingTable = ({ objectives, kpiResolver }) => {
  const [trackingData, setTrackingData] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedObjectives, setExpandedObjectives] = useState(new Set());

  // 🚀 Cargar TODOS los objetivos con tracking en UNA SOLA LLAMADA
  useEffect(() => {
    let cancel = false;
    
    const loadAllTrackingData = async () => {
      setLoading(true);
      try {
        // UNA SOLA LLAMADA al backend
        const response = await objectivesService.getAllObjectivesTracking();
        
        if (cancel) return;

        if (response.success && Array.isArray(response.data)) {
          const trackingMap = {};
          
          response.data.forEach((item) => {
            const objective = item.objective;
            const tracking = item.tracking;
            
            if (tracking?.success && tracking.data) {
              const summary = tracking.data;
              const unit = objective.unit || 'Leads';
              const totalRealValue = computeSummaryValue(unit, summary);

              trackingMap[objective.id] = {
                objectiveId: objective.id,
                totalReal: totalRealValue,
                summary,
                subObjectives: summary.nodesTracking || [],
                tree: item.tree,
                hasTree: item.tree && item.tree.length > 0
              };
            } else {
              trackingMap[objective.id] = {
                objectiveId: objective.id,
                totalReal: 0,
                subObjectives: [],
                tree: item.tree || [],
                hasTree: item.tree && item.tree.length > 0,
                summary: null,
                error: tracking?.error
              };
            }
          });
          
          setTrackingData(trackingMap);
        } else {
          setTrackingData({});
        }
      } catch (error) {
        console.error('❌ Error loading tracking data:', error);
        if (!cancel) setTrackingData({});
      } finally {
        if (!cancel) setLoading(false);
      }
    };

    loadAllTrackingData();
    return () => { cancel = true; };
  }, []);

  const toggleExpand = useCallback((objectiveId) => {
    setExpandedObjectives((prev) => {
      const next = new Set(prev);
      if (next.has(objectiveId)) {
        next.delete(objectiveId);
      } else {
        next.add(objectiveId);
      }
      return next;
    });
  }, []);

  const rows = useMemo(() => {
    return (objectives || []).map((o) => {
      const tracking = trackingData[o.id] || {};
      const summary = tracking.summary || {};
      const expected = calculateExpectedUntilYesterday(o);
      const real = computeSummaryValue(o.unit || 'Leads', summary);
      const pct = calculateProgressPctForUnit(o.unit || 'Leads', expected, real);
      const allowZero = COST_PER_UNITS.has(normalizeUnit(o.unit));

      return {
        ...o,
        __expectedDisplay: formatUnitValue(o.unit, expected, { allowZero }),
        __realDisplay: formatUnitValue(o.unit, real, { allowZero }),
        __progressPct: pct,
        __tracking: tracking,
        __hasSubObjectives: tracking.subObjectives && tracking.subObjectives.length > 0,
        __isExpanded: expandedObjectives.has(o.id),
        __toggleExpand: () => toggleExpand(o.id)
      };
    });
  }, [objectives, trackingData, expandedObjectives, toggleExpand]);

  const table = useAdvanceTable({ data: rows, columns, sortable: true, pagination: true, perPage: 10 });

  return (
    <Card className="h-100">
      <div className="d-flex justify-content-between align-items-center px-3 pt-3 flex-wrap gap-2">
        <h6 className="mb-0">
          Seguimiento de Objetivos (Real vs Esperado)
          {loading && <Spinner size="sm" className="ms-2" animation="border" />}
        </h6>
        <AdvanceTableSearchBox table={table} placeholder="Buscar por cliente, cuenta o unidad..." />
      </div>
      
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando datos de seguimiento...</span>
          </Spinner>
          <p className="text-muted mt-2">Cargando datos de seguimiento desde FactMetaAd...</p>
        </div>
      ) : (
        <>
          <div className="table-responsive px-3">
            <table className="table table-hover">
              <thead className="bg-body-tertiary">
                <tr>
                  <th style={{ width: '30px' }}></th>
                  <th>Cliente</th>
                  <th>Cuenta</th>
                  <th>Medio</th>
                  <th>Período</th>
                  <th>Objetivo</th>
                  <th>Esperado (hasta ayer)</th>
                  <th>Real</th>
                  <th>Progreso</th>
                </tr>
              </thead>
              <tbody>
                {table.getRowModel().rows.map((tableRow) => {
                  const row = tableRow.original;
                  return (
                  <React.Fragment key={row.id}>
                    {/* Fila principal del objetivo */}
                    <tr className={row.__isExpanded ? 'table-active' : ''}>
                      <td>
                        {row.__hasSubObjectives && (
                          <button 
                            className="btn btn-sm btn-link p-0" 
                            onClick={() => row.__toggleExpand()}
                          >
                            <FontAwesomeIcon 
                              icon={row.__isExpanded ? 'chevron-down' : 'chevron-right'} 
                              className="text-primary"
                            />
                          </button>
                        )}
                      </td>
                      <td>{row.clientName}</td>
                      <td>{row.accountName}</td>
                      <td><Badge bg="light" text="dark">{row.medium || 'N/A'}</Badge></td>
                      <td>{row.__expectedDisplay}</td>
                      <td className="fw-semibold">{(row.nominalValue || 0).toLocaleString()} {row.unit || ''}</td>
                      <td className="text-700">{row.__expectedDisplay}</td>
                      <td className="fw-semibold">{row.__realDisplay}</td>
                      <td>
                        {(() => {
                          const pct = clamp(row.__progressPct || 0, 0, 999);
                          let variant = 'secondary';
                          if (pct >= 90 && pct <= 110) variant = 'success';
                          else if (pct < 90) variant = 'warning';
                          else if (pct > 110) variant = 'danger';
                          return (
                            <Badge bg={variant} className="fs-11">
                              <FontAwesomeIcon icon={pct > 110 ? 'arrow-up' : pct < 90 ? 'arrow-down' : 'check'} className="me-1" />
                              {pct.toFixed(1)}%
                            </Badge>
                          );
                        })()}
                      </td>
                    </tr>

                    {/* Filas expandidas de subobjetivos */}
                    {row.__isExpanded && row.__hasSubObjectives && (
                      <tr>
                        <td colSpan="9" className="p-0">
                          <div className="bg-light p-3">
                            <div className="mb-2">
                              <strong className="text-primary">
                                <FontAwesomeIcon icon="sitemap" className="me-2" />
                                Subobjetivos de {row.clientName}
                              </strong>
                            </div>
                            {(() => {
                              const supplementalColumnLabel = getSupplementalColumnMeta(
                                row.unit,
                                { leads: 0, clicks: 0, impressions: 0, videoViews: 0 },
                                0,
                              ).label;

                              return (
                                <table className="table table-sm table-bordered bg-white mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>Subobjetivo</th>
                                  <th>Período</th>
                                  <th className="text-end">Objetivo Asignado</th>
                                  <th className="text-end">Real</th>
                                  <th className="text-end">Progreso</th>
                                  <th className="text-end">Inversión</th>
                                  <th className="text-end">
                                    {supplementalColumnLabel}
                                  </th>
                                  <th>Campañas</th>
                                </tr>
                              </thead>
                              <tbody>
                                {row.__tracking?.subObjectives?.map((subObj, idx) => {
                                  // Buscar el porcentaje del subobjetivo en el árbol
                                  const findNodePercent = (nodes, targetId) => {
                                    for (const node of nodes || []) {
                                      if ((node.id || node.tempId) === targetId) {
                                        return node.percent || 0;
                                      }
                                      if (node.children && node.children.length > 0) {
                                        const found = findNodePercent(node.children, targetId);
                                        if (found !== null) return found;
                                      }
                                    }
                                    return null;
                                  };

                                  const subObjPercent = findNodePercent(row.__tracking?.tree, subObj.nodeId) || 0;
                                  const normalizedUnit = normalizeUnit(row.unit);
                                  const isCostPerMetric = COST_PER_UNITS.has(normalizedUnit);
                                  const assignedObjective = isCostPerMetric
                                    ? Number(row.nominalValue || 0)
                                    : (row.nominalValue || 0) * (subObjPercent / 100);
                                  
                                  const totals = {
                                    leads: Number(subObj.totalLeads || 0),
                                    onsiteLeads: Number(subObj.totalOnsiteLeads || 0),
                                    websiteRegistrations: Number(subObj.totalWebsiteRegistrations || 0),
                                    whatsapp: Number(subObj.totalWhatsapp || 0),
                                    clicks: Number(subObj.totalClicks || 0),
                                    linkClicks: Number(subObj.totalLinkClicks || 0),
                                    impressions: Number(subObj.totalImpressions || 0),
                                    videoViews: Number(subObj.totalVideoViews || 0),
                                    reach: Number(subObj.totalReach || 0),
                                  };
                                  
                                  const totalCost = subObj.campaignDetails?.reduce((sum, c) => sum + (c.cost || 0), 0) || 0;
                                  const realValue = getSubObjectiveRealValue(row.unit, totals, totalCost);
                                  const progressPct = calculateProgressPctForUnit(row.unit || 'Leads', assignedObjective, realValue);
                                  const supplementalMeta = getSupplementalColumnMeta(row.unit, totals, totalCost);

                                  let progressVariant = 'secondary';
                                  if (progressPct >= 90 && progressPct <= 110) progressVariant = 'success';
                                  else if (progressPct < 90) progressVariant = 'warning';
                                  else if (progressPct > 110) progressVariant = 'info';

                                  return (
                                    <tr key={idx}>
                                      <td className="fw-semibold">{subObj.nodeName}</td>
                                      <td>
                                        <small className="text-muted">
                                          {subObj.startDate && subObj.endDate 
                                            ? `${subObj.startDate.split('T')[0]} → ${subObj.endDate.split('T')[0]}` 
                                            : 'Sin fechas'}
                                        </small>
                                      </td>
                                      <td className="text-end text-muted">
                                        {assignedObjective.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} {row.unit}
                                      </td>
                                      <td className="text-end">
                                        <Badge bg="success">
                                          {formatUnitValue(row.unit, realValue, { allowZero: COST_PER_UNITS.has(normalizedUnit) })}
                                        </Badge>
                                      </td>
                                      <td className="text-end">
                                        <Badge bg={progressVariant}>
                                          <FontAwesomeIcon 
                                            icon={progressPct > 110 ? 'arrow-up' : progressPct < 90 ? 'arrow-down' : 'check'} 
                                            className="me-1" 
                                          />
                                          {progressPct.toFixed(1)}%
                                        </Badge>
                                      </td>
                                      <td className="text-end">
                                        ${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </td>
                                      <td className="text-end">
                                        {formatNumber(supplementalMeta.value, {
                                          currency: supplementalMeta.currency,
                                          decimals: supplementalMeta.decimals ?? (supplementalMeta.currency ? 2 : 0),
                                          allowZero: supplementalMeta.allowZero,
                                        })}
                                      </td>
                                      <td>
                                        {subObj.campaignDetails?.map((campaign, cidx) => {
                                          const campaignMetric = getCampaignMetricMeta(row.unit, campaign);
                                          
                                          return (
                                            <div key={cidx} className="small">
                                              <span className="text-muted">{campaign.campaignName}:</span>{' '}
                                              <span className="fw-semibold text-success">
                                                {formatNumber(campaignMetric.value, {
                                                  currency: campaignMetric.currency,
                                                  decimals: campaignMetric.decimals ?? (campaignMetric.currency ? 2 : 0),
                                                  allowZero: false,
                                                })}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                                </table>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-end p-3">
            <AdvanceTableProvider {...table}>
              <AdvanceTablePagination table={table} />
            </AdvanceTableProvider>
          </div>
        </>
      )}
    </Card>
  );
};

export default ObjectivesTrackingTable;


